/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from 'antd/components/radio/Group.tsx'.
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import classNames from 'classnames';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import Radio from './Radio';
import { ConfigContext } from '../config-provider';
import SizeContext from '../config-provider/SizeContext';
import { RadioGroupContextProvider } from 'antd/es/radio/context';
import getDataOrAriaProps from 'antd/es/_util/getDataOrAriaProps';
import warning from 'antd/es/_util/warning';
const RadioGroup = React.forwardRef((props, ref) => {
    const { getPrefixCls, direction } = React.useContext(ConfigContext);
    const size = React.useContext(SizeContext);
    const [value, setValue] = useMergedState(props.defaultValue, {
        value: props.value
    });
    const onRadioChange = (ev) => {
        const lastValue = value;
        const val = ev.target.value;
        if (!('value' in props)) {
            setValue(val);
        }
        const { onChange } = props;
        if (onChange && val !== lastValue) {
            onChange(ev);
        }
    };
    const renderGroup = () => {
        const { prefixCls: customizePrefixCls, className = '', options, optionType, buttonStyle = 'outline', disabled, children, size: customizeSize, style, id, onMouseEnter, onMouseLeave } = props;
        const prefixCls = getPrefixCls('radio', customizePrefixCls);
        const groupPrefixCls = `${prefixCls}-group`;
        let childrenToRender = children;
        if (process.env.NODE_ENV !== 'production')
            warning(buttonStyle !== 'solid', 'Radio.Group', 'B-Design 3.0.0 no longer maintains `solid` buttonStyle.');
        // 如果存在 options, 优先使用
        if (options && options.length > 0) {
            const optionsPrefixCls = optionType === 'button' ? `${prefixCls}-button` : prefixCls;
            childrenToRender = options.map((option) => {
                if (typeof option === 'string' || typeof option === 'number') {
                    // 此处类型自动推导为 string
                    return (React.createElement(Radio, { key: option, prefixCls: optionsPrefixCls, disabled: disabled, value: option, checked: value === option }, option));
                }
                // 此处类型自动推导为 { label: string value: string }
                return (React.createElement(Radio, { key: `radio-group-value-options-${option.value}`, prefixCls: optionsPrefixCls, disabled: option.disabled || disabled, value: option.value, checked: value === option.value, style: option.style }, option.label));
            });
        }
        const mergedSize = customizeSize || size;
        const classString = classNames(groupPrefixCls, `${groupPrefixCls}-${buttonStyle}`, {
            [`${groupPrefixCls}-${mergedSize}`]: mergedSize,
            [`${groupPrefixCls}-rtl`]: direction === 'rtl',
            [`${groupPrefixCls}-container`]: buttonStyle === 'container'
        }, className);
        return (React.createElement("div", { ...getDataOrAriaProps(props), className: classString, style: style, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, id: id, ref: ref }, childrenToRender));
    };
    return (React.createElement(RadioGroupContextProvider, { value: {
            onChange: onRadioChange,
            value,
            disabled: props.disabled,
            name: props.name
        } }, renderGroup()));
});
export default React.memo(RadioGroup);
